import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, RefreshCw, FileDown, ShieldAlert, Trash2, Search as SearchIcon, X } from "lucide-react";
import ImportButton from "@/components/ImportButton";

const RANGES = ["Today", "Yesterday", "This Month", "Last Month", "Last 6 Months", "Custom"];

export default function WorkSummary() {
  const { user } = useAuth();
  const isPrivileged = ["admin", "manager", "branch"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const today = new Date().toISOString().slice(0, 10);

  // --- My today's summary editor ---
  const [mySummary, setMySummary] = useState("");
  const [myQuantity, setMyQuantity] = useState(0);
  const [myLoaded, setMyLoaded] = useState(false);
  const [savedRecord, setSavedRecord] = useState(null);

  const loadMine = async () => {
    try {
      const { data } = await api.get("/work-summaries/today");
      if (data) {
        setMySummary(data.summary || "");
        setMyQuantity(Number(data.total_quantity || 0));
        setSavedRecord(data);
      } else {
        setSavedRecord(null);
      }
    } catch { /* ignore */ }
    setMyLoaded(true);
  };
  useEffect(() => { loadMine(); }, []);

  const saveMine = async () => {
    if (!mySummary.trim()) return toast.error("Please describe today's work first");
    try {
      const { data } = await api.post("/work-summaries", {
        date: today, summary: mySummary.trim(), total_quantity: Number(myQuantity || 0),
      });
      setSavedRecord(data);
      toast.success(savedRecord ? "Work summary updated" : "Work summary saved");
      await refresh();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Save failed"); }
  };

  // --- List / filters ---
  const [rng, setRng] = useState("This Month");
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [userId, setUserId] = useState("all");
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { rng, date_from: dateFrom, date_to: dateTo, q: q || undefined };
      if (isPrivileged) params.user_id = userId;
      const { data } = await api.get("/work-summaries", { params });
      setRows(data);
    } finally { setLoading(false); }
  };
  const refresh = load;

  useEffect(() => {
    if (isPrivileged) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [isPrivileged]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rng, dateFrom, dateTo, userId]);
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const reconcile = async () => {
    const date = window.prompt("Mark absent for date (YYYY-MM-DD):", today);
    if (!date) return;
    try {
      const { data } = await api.post("/work-summaries/reconcile-absent", { date });
      toast.success(`Reconciled ${date} — ${data.marked_absent} staff marked Absent (of ${data.checked_users} active).`);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Reconcile failed"); }
  };

  const removeRow = async (r) => {
    if (!window.confirm(`Delete ${r.user_name}'s summary on ${r.date}?`)) return;
    try {
      await api.delete(`/work-summaries/${r.id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams({ rng, date_from: dateFrom, date_to: dateTo });
      const res = await api.get(`/export/work-summaries?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `work-summaries-${dateFrom}_to_${dateTo}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Export failed"); }
  };

  const exportAll = async () => {
    try {
      const res = await api.get(`/export/work-summaries?all=true`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `work-summaries-ALL.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded ALL work summaries");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Export failed"); }
  };

  return (
    <div className="space-y-4" data-testid="work-summary-page">
      {/* Today's summary card */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-2.5 border-b-2 border-[color:var(--brand-teal)] bg-slate-50 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-slate-900 tracking-tight">Today's Work Summary</div>
            <div className="text-xs text-slate-500 mt-0.5">One line — what you completed today. Missing entries auto-mark you Absent at 11:00 PM.</div>
          </div>
          {savedRecord && (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold uppercase tracking-wider">
              <ShieldAlert className="h-3 w-3" /> Saved · {savedRecord.updated_at?.slice(11, 16)}
            </span>
          )}
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">Summary</Label>
              <Textarea
                value={mySummary}
                onChange={(e) => setMySummary(e.target.value)}
                placeholder='e.g. "PAN card 3, Domicile 2, Follow-ups 5, 1 new customer onboarded"'
                rows={2}
                className="text-sm"
                data-testid="my-summary-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">Total Quantity</Label>
              <Input
                type="number" min="0" step="1"
                value={myQuantity}
                onChange={(e) => setMyQuantity(e.target.value)}
                className="h-10 font-mono-num text-lg font-black"
                data-testid="my-quantity-input"
              />
            </div>
            <Button
              onClick={saveMine}
              disabled={!myLoaded}
              className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              data-testid="save-my-summary-btn"
            >
              <Save className="h-4 w-4 mr-1.5" /> {savedRecord ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      </div>

      {/* Filter toolbar */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">Range</Label>
          <Select value={rng} onValueChange={setRng}>
            <SelectTrigger className="h-9 w-40" data-testid="ws-range"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {rng === "Custom" && (
          <>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" />
            </div>
          </>
        )}
        {isPrivileged && (
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Staff</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="h-9 w-56" data-testid="ws-user-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="all">— All Staff —</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} — {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1 flex-1 min-w-[180px] max-w-xs">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">Search summary / staff</Label>
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-9 pl-8 pr-8" data-testid="ws-search" />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        <Button onClick={load} disabled={loading} className="h-9 bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="ws-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        {isPrivileged && (
          <Button onClick={reconcile} className="h-9 bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="ws-reconcile-btn" title="Mark staff Absent for a chosen date if no summary was saved">
            <ShieldAlert className="h-4 w-4 mr-1.5" /> Mark Absent
          </Button>
        )}
        {isAdmin && (
          <>
            <Button onClick={exportExcel} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="ws-export-btn">
              <FileDown className="h-4 w-4 mr-1.5" /> Export Excel
            </Button>
            <Button onClick={exportAll} className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold" data-testid="ws-export-all-btn" title="Admin: download every historical row in one click (ignores date range)">
              <FileDown className="h-4 w-4 mr-1.5" /> Export ALL
            </Button>
            <ImportButton resource="work-summaries" onDone={load} label="Import Excel" />
          </>
        )}
        <div className="flex-1" />
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Records</div>
          <div className="font-display font-black text-2xl font-mono-num" data-testid="ws-count">{rows.length}</div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm crm-table">
            <thead className="bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Date</th>
                <th className="px-3 py-2 text-left font-semibold">Staff</th>
                <th className="px-3 py-2 text-left font-semibold">Summary</th>
                <th className="px-3 py-2 text-right font-semibold w-24">Qty</th>
                <th className="px-3 py-2 text-left font-semibold w-40">Saved At</th>
                {isPrivileged && <th className="px-3 py-2 text-center font-semibold w-20">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid="ws-row">
                  <td className="px-3 py-2 font-mono-num text-slate-700">{r.date}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">{r.user_name || "—"}</td>
                  <td className="px-3 py-2 text-slate-700 whitespace-pre-wrap break-words max-w-lg">{r.summary}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-bold text-emerald-700">{r.total_quantity || 0}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 font-mono-num">{(r.updated_at || r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  {isPrivileged && (
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => removeRow(r)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`ws-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && !loading && (
            <div className="py-10 text-center text-slate-400 text-sm">No work summaries in the selected range</div>
          )}
        </div>
      </div>
    </div>
  );
}
